import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { IDateFieldSpecs, IFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import { Yii } from "libraries/interfaces/Yii";
import { IMultiselectOptions } from "libraries/module/formbuilder/fields/multiselect/multiselect.model";
import { BtnStyleTypes } from "libraries/ui_button/btn.component";
import { ILabels } from "libraries/interfaces/ILabels";
import { ConfigurationResourceService } from "libraries/service/resources/configuration/configurationResource.service";
import { EFareType } from "libraries/service/resources/airTrip/IGetAirTripQuotes";
import { EFiedsNames } from "libraries/service/resources/air-ticket-resource/air-ticket-resource.service";
import * as moment from 'moment';

interface IRulesSearchFormConfig {
    searchRulesParameters: {
        travellerTypologies: IMultiselectOptions[];
        accountCodes: IMultiselectOptions[];
    };
}

const CONFIG_DEFAULT: IRulesSearchFormConfig = {
    searchRulesParameters: {
        travellerTypologies: [
            {
                label: "Adulte (ADT)",
                value: "ADT"
            },
            {
                label: "Enfant (CNN)",
                value: "CNN"
            },
            {
                label: "B\u00e9b\u00e9 (INF)",
                value: "INF"
            },
            {
                label: "Travailleur Adulte (LBR)",
                value: "LBR"
            },
            {
                label: "Travailleur Enfant (LNN)",
                value: "LNN"
            },
            {
                label: "Travailleur B\u00e9b\u00e9 (LTF)",
                value: "LTF"
            },
            {
                label: "TourOperator Adulte (ITX)",
                value: "ITX"
            },
            {
                label: "TourOperator Enfant (INN)",
                value: "INN"
            },
            {
                label: "TourOperator B\u00e9b\u00e9 (ITF)",
                value: "ITF"
            }
        ],
        accountCodes: [
            {
                label: "BDIS",
                value: "BDIS"
            }
        ]
    }
};

export interface IRulesSearchFormOutput {
    [EFiedsNames.accountCode]: string;
    [EFiedsNames.type]: string;
    [EFiedsNames.whenIssued] : string;
    [EFiedsNames.travellerTypology]: string;
}

@Component({
    selector: 'rules-search-form',
    template: `
        <form [formGroup]="form">
            <div class="col-group">
                <div class="col">
                    <date-field [fieldConfig]="dateFieldConfig"></date-field>
                </div>
                <div class="col">

                </div>
                <div class="col">

                </div>
            </div>
            <div class="col-group">
                <div class="col">
                    <multiselect class="flex-1"
                                 [maxSelection]="1"
                                 [fieldConfig]="typeFieldConfig"
                                 [options]="typeOptions"></multiselect>
                </div>
                <div class="col">
                    <multiselect class="flex-1"
                                 [maxSelection]="1"
                                 [fieldConfig]="accountFieldConfig"
                                 [options]="accountOptions"></multiselect>
                </div>
                <div class="col">
                    <multiselect class="flex-1"
                                 [maxSelection]="1"
                                 [fieldConfig]="travellerTypologyFieldConfig"
                                 [options]="travellerTypologyOptions"></multiselect>
                </div>
            </div>
            <div class="col-group last">
                <div class="right full">
                    <btn [type]="btnType.actionPrincipal"
                         [value]="wording.search"
                         [disableIf]="form.invalid"
                         (onClick)="search()"></btn>
                </div>
            </div>


        </form>
    `,
    styleUrls: ['./rules-search-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RulesSearchFormComponent implements OnInit {

    @Input()
    public whenIssued: string;

    @Output()
    public readonly onSubmit: EventEmitter<IRulesSearchFormOutput> = new EventEmitter<IRulesSearchFormOutput>();

    public btnType: typeof BtnStyleTypes = BtnStyleTypes;
    public dateFieldConfig: IDateFieldSpecs;
    public typeFieldConfig: IFieldSpecs;
    public accountFieldConfig: IFieldSpecs;
    public travellerTypologyFieldConfig: IFieldSpecs;
    public typeOptions: IMultiselectOptions[] = [
        {label: EFareType.public, value: EFareType.public},
        {label: EFareType.negoFare, value: EFareType.negoFare}
    ];
    public accountOptions: IMultiselectOptions[] = [];
    public travellerTypologyOptions: IMultiselectOptions[] = [];

    public form: FormGroup = new FormGroup(
        {
            [EFiedsNames.whenIssued]: new FormControl('', Validators.required),
            [EFiedsNames.type]: new FormControl(''),
            [EFiedsNames.accountCode]: new FormControl(''),
            [EFiedsNames.travellerTypology]: new FormControl(''),
        }
    );

    public wording: ILabels = {
        search: Yii.t('wui-shared-js', 'Rechercher'),
    };

    constructor(
        private readonly configurationResourceService: ConfigurationResourceService
    ) {
    }

    public ngOnInit(): void {

        this.configurationResourceService
            .get(['wui', 'app_sasha', 'config', 'booking-page-ticket', 'traveller-ticket.json'], CONFIG_DEFAULT)
            .subscribe((configuration: IRulesSearchFormConfig) => {
                this.accountOptions = configuration.searchRulesParameters.accountCodes;
                this.travellerTypologyOptions = configuration.searchRulesParameters.travellerTypologies;
            });

        this.dateFieldConfig = {
            formControlName: EFiedsNames.whenIssued,
            formGroup: this.form,
            label: Yii.t('wui-shared-js', "Surcharge de la date d'émission :"),
            icon: 'fa fa-calendar',
        };

        this.typeFieldConfig = {
            formGroup: this.form,
            formControlName: EFiedsNames.type,
            label: Yii.t('wui-shared-js', 'Types de Tarifs :'),
        };
        this.accountFieldConfig = {
            formGroup: this.form,
            formControlName: EFiedsNames.accountCode,
            label: Yii.t('wui-shared-js', 'Account code :'),
        };
        this.travellerTypologyFieldConfig = {
            formGroup: this.form,
            formControlName: EFiedsNames.travellerTypology,
            label: Yii.t('wui-shared-js', 'Typologie de voyageur :'),
        };

        if (this.whenIssued) {
            this.form.get(EFiedsNames.whenIssued).patchValue(
                new Date(this.whenIssued),
                {
                    emitEvent: false
                }
            );
        }

    }

    public search(): void {
        this.onSubmit.emit({
            [EFiedsNames.accountCode] : this.form.get(EFiedsNames.accountCode).value[0],
            [EFiedsNames.whenIssued] : moment(this.form.get(EFiedsNames.whenIssued).value).format('YYYY-MM-DD'),
            [EFiedsNames.type] : this.form.get(EFiedsNames.type).value[0],
            [EFiedsNames.travellerTypology] : this.form.get(EFiedsNames.travellerTypology).value[0],
        });
    }

}
